import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { STATO_META } from "../mocks/personaleDisponibileMock";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import "./PersonaleDisponibileLive.css";
import Lf23Loader from "../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const POLL_INTERVAL_MS = 30_000;
const TIMELINE_PAST_HOURS = 6;
const TIMELINE_FUTURE_HOURS = 12;

const formatTime = (date) =>
    date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Costruisce i 18 slot della timeline (6 passati + 12 futuri) partendo dall'array
// `timeline` pre-calcolato dal backend, allineato in modo che timeline[6] == "now".
const buildTimeline = (operatore, now) => {
    const nowHour = now.getHours();
    const timeline = Array.isArray(operatore.timeline) ? operatore.timeline : [];
    const slots = [];
    for (let i = -TIMELINE_PAST_HOURS; i < TIMELINE_FUTURE_HOURS; i++) {
        const hour = (nowHour + i + 24) % 24;
        const idx = i + TIMELINE_PAST_HOURS;
        const stato = timeline[idx] || "disponibile";
        slots.push({ hour, stato, isNow: i === 0 });
    }
    return slots;
};

const PersonaleDisponibileLive = () => {
    const navigate = useNavigate();

    const [commesseData, setCommesseData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(() => new Date());
    const [collapsedCommesse, setCollapsedCommesse] = useState({});
    const [showInfo, setShowInfo] = useState(false);
    const [fullscreenCommessa, setFullscreenCommessa] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    // Pending filters (UI state)
    const defaultStati = [{ value: "disponibile", label: STATO_META.disponibile.label }];
    const [selectedStati, setSelectedStati] = useState(defaultStati);
    const [selectedCommesse, setSelectedCommesse] = useState([]);
    const [selectedSedi, setSelectedSedi] = useState([]);
    const [selectedTipiContratto, setSelectedTipiContratto] = useState([]);
    // Applied filters
    const [appliedStati, setAppliedStati] = useState(defaultStati);
    const [appliedCommesse, setAppliedCommesse] = useState([]);
    const [appliedSedi, setAppliedSedi] = useState([]);
    const [appliedTipiContratto, setAppliedTipiContratto] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/personale-disponibile/live`);
            setCommesseData(Array.isArray(res.data) ? res.data : []);
            setError(null);
            setLastUpdate(new Date());
        } catch (err) {
            console.error("Errore fetch personale disponibile:", err);
            setError(err.response?.data?.error || err.message || "Errore nel recupero dati");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [fetchData]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape" && fullscreenCommessa) setFullscreenCommessa(null);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [fullscreenCommessa]);

    const toggleCommessa = (commessa) =>
        setCollapsedCommesse((prev) => ({ ...prev, [commessa]: !prev[commessa] }));

    const toggleFullscreen = (commessa) =>
        setFullscreenCommessa((curr) => (curr === commessa ? null : commessa));

    const statoOptions = useMemo(
        () => Object.entries(STATO_META).map(([value, meta]) => ({ value, label: meta.label })),
        []
    );
    const commessaOptions = useMemo(
        () => commesseData.map((c) => ({ value: c.commessa, label: c.commessa })),
        [commesseData]
    );
    const sedeOptions = useMemo(() => {
        const set = new Set();
        commesseData.forEach((c) => c.operatori.forEach((o) => o.sede && set.add(o.sede)));
        return Array.from(set).sort().map((s) => ({ value: s, label: s }));
    }, [commesseData]);
    const tipoContrattoOptions = useMemo(() => {
        const set = new Set();
        commesseData.forEach((c) => c.operatori.forEach((o) => o.tipo_contratto && set.add(o.tipo_contratto)));
        return Array.from(set).sort().map((t) => ({ value: t, label: t }));
    }, [commesseData]);

    const commesse = useMemo(() => {
        const statiFiltro = appliedStati.map((o) => o.value);
        const commesseFiltro = appliedCommesse.map((o) => o.value);
        const sediFiltro = appliedSedi.map((o) => o.value);
        const tipiContrattoFiltro = appliedTipiContratto.map((o) => o.value);
        return commesseData
            .filter((c) => commesseFiltro.length === 0 || commesseFiltro.includes(c.commessa))
            .map((c) => {
                const operatoriFiltrati = c.operatori.filter((o) => {
                    if (statiFiltro.length > 0 && !statiFiltro.includes(o.stato)) return false;
                    if (sediFiltro.length > 0 && !sediFiltro.includes(o.sede)) return false;
                    if (tipiContrattoFiltro.length > 0 && !tipiContrattoFiltro.includes(o.tipo_contratto)) return false;
                    return true;
                });
                return {
                    ...c,
                    operatori: operatoriFiltrati,
                    disponibili: operatoriFiltrati.filter((o) => o.stato === "disponibile").length,
                    totali: operatoriFiltrati.length,
                };
            })
            .filter((c) => c.totali > 0);
    }, [commesseData, appliedStati, appliedCommesse, appliedSedi, appliedTipiContratto]);

    // KPI: dedup per operatore_id (un operatore può comparire in più commesse → array assegn).
    const totaleDisponibili = useMemo(() => {
        const seen = new Set();
        let n = 0;
        commesse.forEach((c) => c.operatori.forEach((o) => {
            if (o.stato === "disponibile" && !seen.has(o.operatore_id)) {
                seen.add(o.operatore_id);
                n++;
            }
        }));
        return n;
    }, [commesse]);
    const totaleOperatori = useMemo(() => {
        const seen = new Set();
        commesse.forEach((c) => c.operatori.forEach((o) => {
            if (!seen.has(o.operatore_id)) seen.add(o.operatore_id);
        }));
        return seen.size;
    }, [commesse]);

    const handleApplyFiltri = () => {
        setAppliedStati(selectedStati);
        setAppliedCommesse(selectedCommesse);
        setAppliedSedi(selectedSedi);
        setAppliedTipiContratto(selectedTipiContratto);
    };

    const handleResetFiltri = () => {
        setSelectedStati(defaultStati);
        setSelectedCommesse([]);
        setSelectedSedi([]);
        setSelectedTipiContratto([]);
        setAppliedStati(defaultStati);
        setAppliedCommesse([]);
        setAppliedSedi([]);
        setAppliedTipiContratto([]);
    };

    return (
        <div className="personale-disp-page">
            <div className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/produttivita")}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Personale Disponibile Live</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Personale realmente disponibile per sostituzioni operative, raggruppato per commessa.
                        </p>
                    </div>
                </div>
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni Personale Disponibile Live</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Pannello live pensato per i <strong>capisquadra</strong>: mostra, per ogni <strong>commessa</strong>, il
                                    personale realmente disponibile per sostituzioni operative. Incrocia <strong>contratti attivi</strong>,
                                    <strong> turni pianificati</strong> e <strong>assenze giustificate</strong> per determinare lo stato corrente di ciascun operatore.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Stati del personale</h4>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li><strong>Disponibile:</strong> contratto attivo, nessun turno in corso e nessuna assenza giustificata in questo istante</li>
                                    <li><strong>In turno:</strong> turno pianificato attivo nell'orario corrente — la cella "Attività / Giustificativo" mostra l'attività del turno e la fascia oraria</li>
                                    <li><strong>Indisponibile:</strong> assenza/giustificativo attivo per l'intera giornata o per la fascia oraria corrente — vince sempre su "in turno" se sovrapposti</li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Timeline 18 ore</h4>
                                <p>
                                    Per ogni operatore vengono mostrati i prossimi <strong>12 slot orari</strong> e i <strong>6 precedenti</strong>,
                                    colorati in base allo stato previsto in quella fascia. Il divisore nero verticale indica l'ora corrente.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Aggiornamento live</h4>
                                <p>
                                    Il pannello si aggiorna automaticamente ogni <strong>30 secondi</strong>. Il timestamp "Ultimo agg." indica
                                    l'orario dell'ultima lettura.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Layout e navigazione</h4>
                                <p>
                                    Ogni commessa è una card collassabile con contatore <strong>disponibili/totali</strong>. Un operatore assegnato
                                    a più commesse nella stessa giornata compare in tutti i gruppi corrispondenti, ma il contatore in alto a destra
                                    lo conta una sola volta.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="personale-disp-toolbar">
                <button
                    type="button"
                    className={`toggle-filters-btn ${showFilters ? "active" : ""}`}
                    onClick={() => setShowFilters((v) => !v)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                <div className="personale-disp-toolbar-right">
                    <div className="personale-disp-summary">
                        <span className="personale-disp-summary-value">{totaleDisponibili}</span>
                        <span className="personale-disp-summary-sep">/</span>
                        <span className="personale-disp-summary-total">{totaleOperatori}</span>
                        <span className="personale-disp-summary-label">personale disponibile</span>
                    </div>
                    <div className="personale-disp-last-update">
                        <span className="personale-disp-live-dot" aria-hidden="true" />
                        Ultimo agg: <strong>{formatTime(lastUpdate)}</strong>
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-grid">
                        <div className="filter-group">
                            <label>Stato</label>
                            <Select
                                isMulti
                                placeholder="Tutti gli stati"
                                options={statoOptions}
                                value={selectedStati}
                                onChange={(selected) => setSelectedStati(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Commessa</label>
                            <Select
                                isMulti
                                placeholder="Tutte le commesse"
                                options={commessaOptions}
                                value={selectedCommesse}
                                onChange={(selected) => setSelectedCommesse(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Sede</label>
                            <Select
                                isMulti
                                placeholder="Tutte le sedi"
                                options={sedeOptions}
                                value={selectedSedi}
                                onChange={(selected) => setSelectedSedi(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Tipo Contratto</label>
                            <Select
                                isMulti
                                placeholder="Tutti i tipi"
                                options={tipoContrattoOptions}
                                value={selectedTipiContratto}
                                onChange={(selected) => setSelectedTipiContratto(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                    </div>
                    <div className="personale-disp-filter-actions">
                        <button
                            type="button"
                            className="personale-disp-btn-primary"
                            onClick={handleApplyFiltri}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Applica
                        </button>
                        <button
                            type="button"
                            className="personale-disp-btn-secondary"
                            onClick={handleResetFiltri}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                            </svg>
                            Reset
                        </button>
                    </div>
                </div>
            )}

            <div className="personale-disp-legend">
                {Object.entries(STATO_META).map(([key, meta]) => (
                    <span key={key} className="personale-disp-legend-item">
                        <span
                            className="personale-disp-badge"
                            style={{ background: meta.bg, color: meta.color, borderColor: meta.color }}
                        >
                            <span className="personale-disp-badge-icon" aria-hidden="true">{meta.icon}</span>
                            {meta.label}
                        </span>
                    </span>
                ))}
            </div>

            {error && (
                <div className="personale-disp-error" role="alert" style={{
                    padding: "12px 16px",
                    margin: "12px 0",
                    background: "#FEE2E2",
                    color: "#991B1B",
                    border: "1px solid #FCA5A5",
                    borderRadius: "8px",
                    fontSize: "14px",
                }}>
                    Errore nel recupero dati: {error}
                </div>
            )}

            {loading && commesseData.length === 0 && !error && (
                <Lf23Loader />
            )}

            {!loading && !error && commesseData.length === 0 && (
                <div style={{ padding: "24px", textAlign: "center", color: "#6B7280" }}>
                    Nessun operatore disponibile.
                </div>
            )}

            <div className="personale-disp-list">
                {commesse.map((commessa) => {
                    const isFullscreen = fullscreenCommessa === commessa.commessa;
                    const isCollapsed = !isFullscreen && !!collapsedCommesse[commessa.commessa];
                    return (
                        <section
                            key={commessa.commessa}
                            className={`personale-disp-sede-card ${isFullscreen ? "fullscreen-chart" : ""}`}
                        >
                            <RotateDevice active={isFullscreen} />
                            <div className="personale-disp-sede-header">
                                <button
                                    type="button"
                                    className="personale-disp-sede-header-toggle"
                                    onClick={() => !isFullscreen && toggleCommessa(commessa.commessa)}
                                    aria-expanded={!isCollapsed}
                                    disabled={isFullscreen}
                                >
                                    <div className="personale-disp-sede-title">
                                        <svg
                                            className={`personale-disp-chevron ${isCollapsed ? "collapsed" : ""}`}
                                            width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2"
                                        >
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                        <span className="personale-disp-sede-name">
                                            {commessa.commessa}
                                        </span>
                                    </div>
                                    <div className="personale-disp-sede-kpi">
                                        <span className="personale-disp-sede-count">
                                            {`${commessa.disponibili} disp / ${commessa.totali} totali`}
                                        </span>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className="personale-disp-fullscreen-btn"
                                    onClick={() => toggleFullscreen(commessa.commessa)}
                                    title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                                    aria-label={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>

                            {!isCollapsed && (
                                <div className="personale-disp-table-wrap">
                                    <table className="personale-disp-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "18%" }}>Nome Cognome</th>
                                                <th style={{ width: "14%" }}>Tipo Contratto</th>
                                                <th style={{ width: "12%" }}>Sede</th>
                                                <th style={{ width: "12%" }}>Stato</th>
                                                <th style={{ width: "20%" }}>Attività / Giustificativo</th>
                                                <th style={{ width: "24%" }}>Timeline 18h (-6h / +12h)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {commessa.operatori.map((o) => {
                                                const meta = STATO_META[o.stato] || STATO_META.disponibile;
                                                const timeline = buildTimeline(o, lastUpdate);
                                                return (
                                                    <tr key={o.operatore_id}>
                                                        <td className="personale-disp-targa">{o.nome_cognome}</td>
                                                        <td>{o.tipo_contratto || "—"}</td>
                                                        <td>{o.sede || "—"}</td>
                                                        <td>
                                                            <span
                                                                className="personale-disp-badge"
                                                                style={{
                                                                    background: meta.bg,
                                                                    color: meta.color,
                                                                    borderColor: meta.color,
                                                                }}
                                                            >
                                                                <span className="personale-disp-badge-icon" aria-hidden="true">
                                                                    {meta.icon}
                                                                </span>
                                                                {meta.label}
                                                            </span>
                                                        </td>
                                                        <td className="personale-disp-motivo">
                                                            {o.attivita_giustificativo || <span className="personale-disp-motivo-empty">—</span>}
                                                        </td>
                                                        <td className="personale-disp-timeline-cell">
                                                            <div
                                                                className="personale-disp-timeline"
                                                                role="img"
                                                                aria-label={`Timeline 18 ore per ${o.nome_cognome}`}
                                                            >
                                                                {timeline.map((slot, idx) => (
                                                                    <div key={idx} className="personale-disp-timeline-col">
                                                                        <span className={`personale-disp-timeline-hour ${slot.isNow ? "now" : ""}`}>
                                                                            {String(slot.hour).padStart(2, "0")}
                                                                        </span>
                                                                        <span
                                                                            className={`personale-disp-timeline-slot stato-${slot.stato} ${slot.isNow ? "now" : ""}`}
                                                                            title={`${String(slot.hour).padStart(2, "0")}:00 — ${STATO_META[slot.stato]?.label || slot.stato}`}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

export default PersonaleDisponibileLive;
